import { useEffect, useState } from 'react';
import { useTheme } from './ThemeProvider';
import { buildCitaDateTimeISO, isoToFechaHoraLocal } from '../utils/citaAgenda';
import CitaSelectorHora from './cita/CitaSelectorHora';

/**
 * Edición ligera de cita de captación (fecha / hora / duración).
 * Reutiliza el mismo estilo que ModalTransicionEtapa.
 */
export default function ModalEditarCitaCaptacion({
  isOpen,
  cita,
  captacion,
  onClose,
  onGuardar,
  onCancelarCita,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';
  const [form, setForm] = useState({ fecha: '', hora: '', duracion: '1' });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !cita) return;
    const { fecha, hora } = isoToFechaHoraLocal(cita.fecha_inicio);
    const fin = new Date(cita.fecha_fin || cita.fecha_inicio);
    const ini = new Date(cita.fecha_inicio);
    const horas = Number.isNaN(fin.getTime()) || Number.isNaN(ini.getTime())
      ? 1
      : Math.max(0.5, Math.round(((fin.getTime() - ini.getTime()) / 3600000) * 2) / 2);
    setForm({
      fecha: fecha || '',
      hora: hora || '10:00',
      duracion: String(horas),
    });
    setError('');
    setGuardando(false);
  }, [isOpen, cita?.id, cita?.fecha_inicio, cita?.fecha_fin]);

  if (!isOpen || !cita) return null;

  const nombre = [captacion?.nombre, captacion?.apellido].filter(Boolean).join(' ')
    || cita.titulo
    || 'Captación';
  const cancelada = ['cancelada', 'cancelado'].includes(String(cita.estado || '').toLowerCase());

  const inp = {
    width: '100%', padding: '8px 10px', borderRadius: 8,
    border: `1px solid ${c.border}`, background: c.bgInput,
    color: c.textPrimary, fontSize: 13, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const lbl = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    display: 'block', marginBottom: 5,
    textTransform: 'uppercase', letterSpacing: '0.06em',
  };

  const guardar = async () => {
    if (!form.fecha || !form.hora) {
      setError('Indica fecha y hora');
      return;
    }
    const fechaInicio = buildCitaDateTimeISO(form.fecha, form.hora);
    if (!fechaInicio) {
      setError('Fecha u hora no válidas');
      return;
    }
    const durH = parseFloat(form.duracion) || 1;
    const fechaFin = new Date(new Date(fechaInicio).getTime() + durH * 3600000).toISOString();
    setGuardando(true);
    setError('');
    try {
      await onGuardar({
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        duracion_horas: durH,
      });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'No se pudo guardar');
      setGuardando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    }}
      onClick={onClose}
    >
      <div
        style={{
          background: theme.name === 'dark' ? c.bgCard : '#c8c8be',
          border: `1px solid ${theme.name === 'dark' ? c.border : '#a8a8a0'}`,
          borderRadius: 16, width: 420, maxWidth: '95vw',
          boxShadow: theme.name === 'dark' ? '0 24px 64px rgba(0,0,0,0.45)' : '0 16px 48px rgba(0,0,0,0.30)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ height: 4, background: 'linear-gradient(90deg,#f59e0b,#ef4444)' }} />
        <div style={{ padding: '16px 18px 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 36, height: 36, borderRadius: 10, display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 18,
            background: 'rgba(245,158,11,0.15)',
          }}>📅</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary }}>
              {cancelada ? 'Cita anulada' : 'Editar cita'}
            </div>
            <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2 }}>{nombre}</div>
          </div>
          <button type="button" onClick={onClose} style={{
            background: 'none', border: 'none', color: c.textMuted,
            fontSize: 22, cursor: 'pointer', lineHeight: 1,
          }}>×</button>
        </div>

        <div style={{ padding: '8px 18px 18px' }}>
          {cancelada && (
            <div style={{
              marginBottom: 12, padding: '8px 10px', borderRadius: 8, fontSize: 12,
              background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)',
            }}>
              Esta cita está cancelada. Puedes consultar la fecha, pero no editarla.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.9fr 0.7fr', gap: 8, marginBottom: 12, alignItems: 'end' }}>
            <div>
              <label style={lbl}>Fecha</label>
              <input type="date" value={form.fecha} disabled={cancelada}
                onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))}
                style={{ ...inp, opacity: cancelada ? 0.65 : 1 }} />
            </div>
            <div>
              <label style={lbl}>Hora</label>
              <CitaSelectorHora
                value={form.hora || '10:00'}
                onChange={(v) => setForm((p) => ({ ...p, hora: v }))}
                c={c}
                isDark={isDark}
                accentColor="#f59e0b"
                accentBg="rgba(245,158,11,0.15)"
                chipText="#fbbf24"
                borderColor={c.border}
                align="right"
                disabled={cancelada}
                zIndex={12050}
              />
            </div>
            <div>
              <label style={lbl}>Horas</label>
              <input type="number" min="0.5" max="8" step="0.5" value={form.duracion} disabled={cancelada}
                onChange={(e) => setForm((p) => ({ ...p, duracion: e.target.value }))}
                style={{ ...inp, opacity: cancelada ? 0.65 : 1 }} />
            </div>
          </div>

          {error && (
            <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 10 }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {!cancelada && typeof onCancelarCita === 'function' && (
              <button type="button" onClick={onCancelarCita} disabled={guardando}
                style={{
                  marginRight: 'auto', padding: '8px 12px', borderRadius: 8,
                  border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.08)',
                  color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}>
                Anular cita
              </button>
            )}
            <button type="button" onClick={onClose}
              style={{
                padding: '8px 14px', borderRadius: 8, border: `1px solid ${c.border}`,
                background: c.bgInput, color: c.textSecondary, fontSize: 12,
                fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}>
              Cerrar
            </button>
            {!cancelada && (
              <button type="button" onClick={guardar} disabled={guardando}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: 'linear-gradient(135deg,#f59e0b,#ef4444)',
                  color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  fontFamily: 'inherit', opacity: guardando ? 0.7 : 1,
                }}>
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
